import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, TrendingUp, Clock, CheckCircle, Package, DollarSign } from 'lucide-react';
import PDFProcessor from '../PDFProcessor/PDFProcessor';

interface ProcessedInvoice {
  id: string;
  vendor: string;
  amount: number;
  date: string;
  lineItems: Array<{
    description: string;
    quantity: number;
    price: number;
  }>;
  processingTime: number;
  accuracy: number;
}

const InvoiceProcessing: React.FC = () => {
  const [processedInvoices, setProcessedInvoices] = useState<ProcessedInvoice[]>([]);
  const [totalSaved, setTotalSaved] = useState(0);
  const [totalProcessed, setTotalProcessed] = useState(0);

  const handleProcessingComplete = (result: any) => {
    const invoice: ProcessedInvoice = {
      id: Date.now().toString(),
      vendor: result.vendor || 'Unknown Vendor',
      amount: result.amount || 0,
      date: result.date || new Date().toISOString().split('T')[0],
      lineItems: result.lineItems || [],
      processingTime: result.processingTime,
      accuracy: result.accuracy
    };

    setProcessedInvoices(prev => [...prev, invoice]);
    setTotalProcessed(prev => prev + 1);

    // Calculate time saved (310x faster than manual entry)
    const manualEntryTime = 15 * 60 * 1000; // 15 minutes in ms
    const timeSaved = manualEntryTime - result.processingTime;
    setTotalSaved(prev => prev + timeSaved);
  };

  const formatTime = (ms: number) => {
    const hours = Math.floor(ms / (1000 * 60 * 60));
    const minutes = Math.floor((ms % (1000 * 60 * 60)) / (1000 * 60));
    return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Invoice Processing
        </h1>
        <p className="text-gray-600">
          Upload and process invoices 310x faster with Wayne IA
        </p>
      </motion.div>

      {/* Stats Dashboard */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Invoices Processed</p>
              <p className="text-2xl font-bold text-gray-900">{totalProcessed}</p>
            </div>
            <FileText className="w-8 h-8 text-blue-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Time Saved</p>
              <p className="text-2xl font-bold text-gray-900">{formatTime(totalSaved)}</p>
            </div>
            <Clock className="w-8 h-8 text-green-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Processing Speed</p>
              <p className="text-2xl font-bold text-gray-900">310x</p>
            </div>
            <TrendingUp className="w-8 h-8 text-yellow-500" />
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.4 }}
          className="bg-white rounded-lg shadow-md p-6"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Accuracy</p>
              <p className="text-2xl font-bold text-gray-900">95%</p>
            </div>
            <CheckCircle className="w-8 h-8 text-purple-500" />
          </div>
        </motion.div>
      </div>

      {/* PDF Processor */}
      <div className="mb-8">
        <PDFProcessor onProcessingComplete={handleProcessingComplete} />
      </div>

      {/* Processed Invoices */}
      {processedInvoices.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-lg shadow-xl p-6"
        >
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Recent Invoices
          </h2>
          <div className="space-y-4">
            {processedInvoices.map((invoice, index) => (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
                className="border-l-4 border-blue-500 pl-4 py-3"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{invoice.vendor}</h3>
                    <p className="text-sm text-gray-600">
                      {invoice.date} • {invoice.lineItems.length} items
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-xl font-bold text-gray-900">
                      ${invoice.amount.toFixed(2)}
                    </p>
                    <p className="text-xs text-green-600">
                      Processed in {invoice.processingTime}ms
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-gray-500" />
                <span className="text-sm text-gray-600">
                  Total invoices: {processedInvoices.length}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-gray-500" />
                <span className="text-sm font-semibold text-gray-900">
                  Total: ${processedInvoices.reduce((sum, inv) => sum + inv.amount, 0).toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default InvoiceProcessing;